import {type FC, type RefObject, useCallback, useEffect, useMemo, useReducer, useRef, useState} from 'react';
import type {JitsiTrackState} from '../types/lib-jitsi-meet';
import useStreamingStore from '../../../store/useStreamingStore.ts';
import CenterWait from '../../../assets/broadcast/center-wait.jpg';
import {evaluateTrackPriority, isVideoPlaying, isVideoVisiblyDark} from '../../../utils/StreamingUtil.ts';

interface RemoteCellProps {
    remoteElemRef: RefObject<Array<HTMLVideoElement>>;
    remoteTracks: RefObject<Map<string, JitsiTrackState>>;
    remoteTrackVersion: number;
    remoteSize: number;
    isWatching?: boolean;
    displayedParticipants?: RefObject<Set<string>>;
    onConstraintUpdate?: () => void;
}

interface TrackPriority {
    trackId: string;
    trackState: JitsiTrackState;
    priority: number;
}

interface CachedPriority {
    priority: number;
    lastUpdated: number;
    lastVideoCheck: number;
}

const PRIORITY_CACHE_DURATION = 15000;
const VIDEO_CHECK_INTERVAL = 40000;
const ROTATION_INTERVAL = 20000;

const RemoteCell: FC<RemoteCellProps> = ({
    remoteElemRef,
    remoteTracks,
    remoteTrackVersion,
    remoteSize,
    isWatching = false,
    displayedParticipants,
    onConstraintUpdate,
}) => {
    const {state, gridLayout} = useStreamingStore();
    const rotationIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const remoteCellId = useRef<Array<string>>(Array(remoteSize).fill(''));
    const remoteCellName = useRef<Array<string>>(Array(remoteSize).fill('-'));
    const hiddenTrackMaps = useRef<Map<string, number>>(new Map());
    const priorityCache = useRef<Map<string, CachedPriority>>(new Map());
    const [, forceUpdate] = useReducer((x) => x + 1, 0);
    const isAllOK = useRef<boolean>(false);

    const getDisplayName = useCallback(
        (trackId: string): string => {
            if (!state.conference || !trackId) return '-';
            return state.conference.getParticipantById(trackId)?.getDisplayName() ?? '-';
        },
        [state.conference]
    );

    const remoteTracksArray = useMemo(() => {
        if (!remoteTracks.current) return [];
        console.log('전체 접속자 수: ', remoteTracks.current.size);
        isAllOK.current = false;
        return Array.from(remoteTracks.current.entries());
    }, [remoteTrackVersion]);

    const getBasePriority = useCallback((trackState: JitsiTrackState): number => {
        let priority = 0;
        if (trackState.videoTrack) {
            priority += 150;
        }
        if (trackState.audioTrack) {
            priority += 10;
        }
        if (trackState.videoTrack === null) {
            priority -= 200;
        }
        return priority;
    }, []);

    const getVideoBonus = useCallback((trackState: JitsiTrackState, videoElement: HTMLVideoElement): number => {
        if (!videoElement || !trackState.videoTrack) return 0;
        let bonus = 0;
        if (isVideoPlaying(videoElement)) {
            bonus += 100;
        }
        if (!isVideoVisiblyDark(videoElement)) {
            bonus += 50;
        }
        return bonus;
    }, []);

    // 캐쉬에서 가져오기
    const getCachedPriority = useCallback(
        (trackId: string, trackState: JitsiTrackState, videoElement?: HTMLVideoElement): number => {
            const now = Date.now();
            const cached = priorityCache.current.get(trackId);

            // 캐쉬
            if (cached && now - cached.lastUpdated < PRIORITY_CACHE_DURATION) {
                if (videoElement && now - cached.lastVideoCheck > VIDEO_CHECK_INTERVAL) {
                    const videoBonus = getVideoBonus(trackState, videoElement);
                    const basePriority = getBasePriority(trackState);
                    const newPriority = basePriority + videoBonus;
                    priorityCache.current.set(trackId, {
                        priority: newPriority,
                        lastUpdated: cached.lastUpdated,
                        lastVideoCheck: now,
                    });
                    return newPriority;
                }
                return cached.priority;
            }

            // 노 캐쉬
            const priority = evaluateTrackPriority(trackId, trackState, videoElement);
            priorityCache.current.set(trackId, {
                priority,
                lastUpdated: now,
                lastVideoCheck: now,
            });
            return priority;
        },
        []
    );

    const getSortedTracks = useCallback((): Array<TrackPriority> => {
        if (!remoteTracks.current || !remoteElemRef.current) return [];
        const tracksWithPriority: Array<TrackPriority> = [];
        const currentlyDisplayed = new Set(remoteCellId.current.filter((id) => id));
        for (const [trackId, trackState] of remoteTracksArray) {
            let priority;
            if (hiddenTrackMaps.current.has(trackId)) {
                // console.log("이 로그는 안 나오는게 정상");
                priority = hiddenTrackMaps.current.get(trackId) ?? 100;
            } else if (currentlyDisplayed.has(trackId)) {
                const currentIndex = remoteCellId.current.findIndex((id) => id === trackId);
                const videoElement = currentIndex >= 0 ? remoteElemRef.current?.[currentIndex] : undefined;
                priority = getCachedPriority(trackId, trackState, videoElement);
            } else {
                priority = getCachedPriority(trackId, trackState);
            }
            tracksWithPriority.push({
                trackId,
                trackState,
                priority,
            });
        }
        return tracksWithPriority.sort((a, b) => b.priority - a.priority);
    }, [remoteTracksArray, getCachedPriority]);

    const attachAllTracks = useCallback(() => {
        if (!remoteTracks.current || !remoteElemRef.current) return;
        const sortedTracks = getSortedTracks();
        const availableTrackIds = new Set(sortedTracks.map((t) => t.trackId));
        const newAssignments = [...remoteCellId.current];
        let changed = false;

        // 1) 더 이상 존재하지 않거나 videoTrack이 죽은 트랙은 해당 셀에서 detach & 비움
        for (let i = 0; i < remoteSize; i++) {
            const currentId = newAssignments[i];
            if (!currentId) continue;
            const currentTrackState = remoteTracks.current?.get(currentId);
            const isGone = !availableTrackIds.has(currentId) || !currentTrackState;
            const isVideoDead = !!currentTrackState && !currentTrackState.videoTrack;
            if (isGone || isVideoDead) {
                const videoElement = remoteElemRef.current?.[i];
                const audioElement = document.getElementById(`remote-audio-${i}`);
                if (currentTrackState) {
                    if (videoElement && currentTrackState.videoTrack) {
                        currentTrackState.videoTrack.detach(videoElement);
                    }
                    if (audioElement && currentTrackState.audioTrack) {
                        currentTrackState.audioTrack.detach(audioElement);
                    }
                }
                priorityCache.current.delete(currentId);
                hiddenTrackMaps.current.delete(currentId);
                newAssignments[i] = '';
                remoteCellName.current[i] = '-';
                changed = true;
            }
        }

        // 2) 이미 할당된 트랙 id
        const assignedIds = new Set(newAssignments.filter((id) => id));

        // 3) 빈 셀을 우선순위 높은 미할당 트랙으로 채움 (기존 셀은 건드리지 않음 → 깜빡임 없음)
        const unassignedTracks = sortedTracks.filter((t) => !assignedIds.has(t.trackId));
        let cursor = 0;
        for (let i = 0; i < remoteSize; i++) {
            if (newAssignments[i]) continue;
            while (cursor < unassignedTracks.length && assignedIds.has(unassignedTracks[cursor].trackId)) {
                cursor++;
            }
            if (cursor >= unassignedTracks.length) break;
            const track = unassignedTracks[cursor++];
            newAssignments[i] = track.trackId;
            assignedIds.add(track.trackId);
            hiddenTrackMaps.current.delete(track.trackId);

            const videoElement = remoteElemRef.current?.[i];
            const audioElement = document.getElementById(`remote-audio-${i}`);
            remoteCellName.current[i] = state.conference ? getDisplayName(track.trackId) : '-';
            if (videoElement && track.trackState.videoTrack) {
                track.trackState.videoTrack.attach(videoElement);
            }
            if (audioElement && track.trackState.audioTrack) {
                track.trackState.audioTrack.attach(audioElement);
            }
            changed = true;
        }

        remoteCellId.current = newAssignments;
        if (changed) forceUpdate();
    }, [getSortedTracks, remoteSize, getDisplayName, state.conference]);

    const detachAllTracks = useCallback(() => {
        if (!remoteTracks.current || !remoteElemRef.current) return;
        remoteTracksArray.forEach(([trackId, trackState]) => {
            for (let index = 0; index < remoteSize; index++) {
                const videoElement = remoteElemRef.current?.[index];
                const audioElement = document.getElementById(`remote-audio-${index}`);
                if (videoElement && trackState.videoTrack) {
                    trackState.videoTrack.detach(videoElement);
                }
                if (audioElement && trackState.audioTrack) {
                    trackState.audioTrack.detach(audioElement);
                }
            }
        });
        remoteCellId.current = Array(remoteSize).fill('');
        remoteCellName.current = Array(remoteSize).fill('-');
        hiddenTrackMaps.current.clear();
        priorityCache.current.clear();
        forceUpdate();
    }, [remoteTracksArray, remoteSize]);

    const attachTrackToCell = (track: TrackPriority, cellIndex: number, displayName: string) => {
        const videoElement = remoteElemRef.current?.[cellIndex];
        const audioElement = document.getElementById(`remote-audio-${cellIndex}`);

        if (videoElement && track.trackState.videoTrack) {
            track.trackState.videoTrack.attach(videoElement);
        }
        if (audioElement && track.trackState.audioTrack) {
            track.trackState.audioTrack.attach(audioElement);
        }
        remoteCellId.current[cellIndex] = track.trackId;
        remoteCellName.current[cellIndex] = displayName;
        hiddenTrackMaps.current.delete(track.trackId);
        forceUpdate();
    };

    const detachTrackFromCell = (cellIndex: number) => {
        const trackId = remoteCellId.current[cellIndex];
        if (!trackId) return;
        const trackState = remoteTracks.current?.get(trackId);
        if (!trackState) return;
        const videoElement = remoteElemRef.current?.[cellIndex];
        const audioElement = document.getElementById(`remote-audio-${cellIndex}`);
        const priority = getCachedPriority(trackId, trackState, videoElement);
        hiddenTrackMaps.current.set(trackId, priority);
        if (videoElement && trackState.videoTrack) {
            trackState.videoTrack.detach(videoElement);
        }
        if (audioElement && trackState.audioTrack) {
            trackState.audioTrack.detach(audioElement);
        }
        remoteCellId.current[cellIndex] = '';
        remoteCellName.current[cellIndex] = '-';
        forceUpdate();
    };

    const rotationByPolicy = useCallback(() => {
        if (isAllOK.current) {
            console.log('모두 정상이므로 더이상 rotation 을 하지 않습니다.');
            return;
        }
        const allTracks = getSortedTracks();
        // 유효 트랙
        const validTracks = allTracks.filter((track) => track.priority > 0);
        if (validTracks.length === 0 || remoteTracksArray.length <= remoteSize) {
            return;
        }
        // 현재 그리드
        const currentGridTrackIds = new Set(remoteCellId.current.filter((id) => id));
        // 현재 그리드에 없는 여분 트랙
        const hiddenTracks = validTracks.filter((track) => !currentGridTrackIds.has(track.trackId));
        if (hiddenTracks.length === 0) return;
        const maxHiddenPriority = Math.max(...hiddenTracks.map((track) => track.priority));
        if (maxHiddenPriority <= 110) return;
        const currentGridTracks = remoteCellId.current
            .map((trackId, index) => {
                if (!trackId) return null;
                const trackState = remoteTracks.current?.get(trackId);
                if (!trackState) return null;
                const videoElement = remoteElemRef.current?.[index];
                const priority = getCachedPriority(trackId, trackState, videoElement);
                return {index, trackId, priority};
            })
            .filter((track): track is NonNullable<typeof track> => track !== null);
        if (currentGridTracks.length === 0) return;
        // 현재 그리드에서 최소 점수 와 트랙들, 랜덤한 트랙
        const minCurrentPriority = Math.min(...currentGridTracks.map((track) => track.priority));
        const lowestPriorityTracks = currentGridTracks.filter((track) => track.priority === minCurrentPriority);
        if (maxHiddenPriority <= minCurrentPriority + 20) {
            isAllOK.current = true;
            return;
        }
        const targetCell = lowestPriorityTracks[Math.floor(Math.random() * lowestPriorityTracks.length)];
        // 여분 트랙 중 최고 점수 트랙들
        const bestHiddenTracks = hiddenTracks.filter((track) => track.priority === maxHiddenPriority);
        const selectedHiddenTrack = bestHiddenTracks[Math.floor(Math.random() * bestHiddenTracks.length)];
        let flag = true;
        const calIndex = [-11, -10, -9, -1, 0, +1, +9, +10, 11];
        console.log('대상 경로당 명: ', getDisplayName(selectedHiddenTrack.trackId));
        for (let i = 0; i < 9; i++) {
            if (remoteCellId.current[targetCell.index + calIndex[i]]) {
                console.log(
                    '계산 중인 경로당 명: ',
                    getDisplayName(remoteCellId.current[targetCell.index + calIndex[i]])
                );
                if (remoteCellId.current[targetCell.index + calIndex[i]] === selectedHiddenTrack.trackId) flag = false;
            }
        }
        if (flag) {
            console.log(
                `교체: ${getDisplayName(targetCell.trackId)} (${targetCell.priority}) -> ${getDisplayName(selectedHiddenTrack.trackId)} (${selectedHiddenTrack.priority})`
            );
            detachTrackFromCell(targetCell.index);
            const targetDisplayName = getDisplayName(selectedHiddenTrack.trackId);
            attachTrackToCell(selectedHiddenTrack, targetCell.index, targetDisplayName);
        } else {
            console.log('targetCell 과 selectedHiddenTrack 이 가까이 있기 때문에 skip');
        }
    }, [getSortedTracks, getCachedPriority, getDisplayName, remoteTracksArray.length, remoteSize, remoteCellId]);

    const cleanupCache = useCallback(() => {
        const now = Date.now();
        const currentTrackIds = new Set(remoteTracksArray.map(([trackId]) => trackId));
        for (const [trackId, cached] of priorityCache.current.entries()) {
            if (!currentTrackIds.has(trackId) || now - cached.lastUpdated > PRIORITY_CACHE_DURATION * 3) {
                priorityCache.current.delete(trackId);
            }
        }
        for (const trackId of hiddenTrackMaps.current.keys()) {
            if (!currentTrackIds.has(trackId)) {
                hiddenTrackMaps.current.delete(trackId);
            }
        }
    }, [remoteTracksArray]);

    // 레이아웃 전환으로 remoteSize가 바뀌면 잘려나간 셀의 할당이 남아 복귀 시 새 <video>에 attach가 안 된다.
    // 아래 attach effect보다 먼저 선언되어야 같은 커밋에서 초기화 후 전체 재배치된다.
    const prevRemoteSizeRef = useRef(remoteSize);
    useEffect(() => {
        if (prevRemoteSizeRef.current === remoteSize) return;
        if (remoteTracks.current) {
            for (let i = 0; i < prevRemoteSizeRef.current; i++) {
                const trackState = remoteTracks.current.get(remoteCellId.current[i]);
                if (!trackState) continue;
                // 언마운트된 element의 stale ref 대신 DOM에서 직접 조회
                const videoElement = document.getElementById(`remote-video-${i}`) as HTMLVideoElement | null;
                const audioElement = document.getElementById(`remote-audio-${i}`);
                if (videoElement && trackState.videoTrack) trackState.videoTrack.detach(videoElement);
                if (audioElement && trackState.audioTrack) trackState.audioTrack.detach(audioElement);
            }
        }
        if (remoteElemRef.current && remoteSize < prevRemoteSizeRef.current) {
            remoteElemRef.current.length = remoteSize;
        }
        prevRemoteSizeRef.current = remoteSize;
        remoteCellId.current = Array(remoteSize).fill('');
        remoteCellName.current = Array(remoteSize).fill('-');
        hiddenTrackMaps.current.clear();
        priorityCache.current.clear();
        isAllOK.current = false;
    }, [remoteSize]);

    // 트랙 변경 시 증분 반영 (기존 셀은 유지 → 깜빡임 없음)
    useEffect(() => {
        attachAllTracks();
    }, [attachAllTracks]);

    // 셀 할당이 바뀌는 경로(증분 배치·로테이션·레이아웃 초기화)가 여럿이라 렌더마다 비교해 한 곳에서 동기화한다.
    // attach effect보다 뒤에 선언되어야 같은 커밋의 최종 배치를 본다.
    useEffect(() => {
        if (!displayedParticipants?.current) return;
        const assigned = remoteCellId.current.filter((id) => id);
        const current = displayedParticipants.current;
        if (assigned.length === current.size && assigned.every((id) => current.has(id))) return;
        current.clear();
        for (const id of assigned) current.add(id);
        onConstraintUpdate?.();
    });

    // 회전/캐시 정리 인터벌 (콜백 변경 시 최신 클로저 유지)
    useEffect(() => {
        rotationIntervalRef.current = setInterval(() => {
            rotationByPolicy();
            cleanupCache();
        }, ROTATION_INTERVAL);
        return () => {
            if (rotationIntervalRef.current) {
                clearInterval(rotationIntervalRef.current);
                rotationIntervalRef.current = null;
            }
        };
    }, [rotationByPolicy, cleanupCache]);

    // 언마운트 시에만 전체 detach
    useEffect(() => {
        return () => {
            detachAllTracks();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            {Array.from({length: remoteSize}).map((_, index) => {
                return (
                    <div
                        id={`remote-cell-${index}`}
                        style={{
                            width: '100%',
                            padding: '0',
                            position: 'relative',
                            backgroundColor: '#1c1c1c',
                            overflow: 'hidden',
                            backgroundImage: `url(${CenterWait})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                        }}
                        key={index}
                    >
                        <video
                            id={`remote-video-${index}`}
                            autoPlay={true}
                            muted={true}
                            playsInline={true}
                            style={{
                                display: 'block',
                                objectFit: 'cover',
                                padding: '0',
                                width: '100%',
                                height: '100%',
                                aspectRatio: 16 / 9,
                                ...(isWatching ? {} : {position: 'relative', zIndex: 1}),
                            }}
                            crossOrigin="anonymous"
                            ref={(el) => {
                                if (remoteElemRef.current) {
                                    remoteElemRef.current[index] = el as HTMLVideoElement;
                                }
                            }}
                        />
                        <audio id={`remote-audio-${index}`} autoPlay={true} />
                        {gridLayout.sub ? (
                            <div
                                className="participant-name"
                                style={{
                                    maxWidth: '95%',
                                    position: 'absolute',
                                    ...(isWatching ? {} : {zIndex: 2}),
                                    bottom: '0',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    fontSize: '1rem',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                    padding: '0.1rem',
                                }}
                            >
                                {remoteCellName.current[index]}
                            </div>
                        ) : (
                            <div></div>
                        )}
                    </div>
                );
            })}
        </>
    );
};

export default RemoteCell;
